import { Localization } from "@viamap/viamap2-common";
import { getPLAAlertDetails, resolvePLACalculatedAttributes } from "./PLAExpression";
import { getPLAHierarchicalRelations, PLAElementRecord, PLAProject, PLARelationRecord } from "./PLAState";
import { formatPLANumber } from "./PLAFormatting";

export const PLA_OPTIMIZER_TARGET_ATTRIBUTE = "VærdiAfUudnyttedeByggeretter";

export type PLAOptimizerChange = {
   id: string;
   anvendelseElementId: string;
   anvendelseName: string;
   etagearealAttributeId: string;
   byggefeltElementId?: string;
   byggefeltName?: string;
   currentEtageareal: number;
   proposedEtageareal: number;
   etagearealChange: number;
};

export type PLAOptimizerProposal = {
   id: string;
   topElementId: string;
   topElementName: string;
   currentValue: number;
   remainingValue: number;
   partial: boolean;
   actionText: string;
   details: string;
   changes: PLAOptimizerChange[];
   expressionTrace: string[];
};

export type PLAOptimizerWarningLimitStat = {
   id: string;
   message: string;
   count: number;
   examples: string[];
};

export type PLAOptimizationResult = {
   proposals: PLAOptimizerProposal[];
   warningLimitStats: PLAOptimizerWarningLimitStat[];
   parameters: Required<PLAOptimizerParameters>;
};

export type PLAOptimizerAlgorithm = "greedy" | "astar";
export type PLAOptimizerGoal = "minimize" | "maximize";

export type PLAOptimizerParameters = {
   targetAttributeId?: string;
   goal?: PLAOptimizerGoal;
   algorithm?: PLAOptimizerAlgorithm;
   changeElementTypeName?: string;
   changeAttributeName?: string;
};

export type PLAOptimizationProgress = {
   current: number;
   total: number;
   percent: number;
   message: string;
};

export type PLAOptimizationOptions = {
   onProgress?: (progress: PLAOptimizationProgress) => void;
   yieldEveryNodes?: number;
} & PLAOptimizerParameters;

type AnvendelseCandidate = {
   element: PLAElementRecord;
   etagearealAttributeId: string;
   currentEtageareal: number;
   effectPerEtageareal?: number;
   containingByggefelt?: PLAElementRecord;
   path: PLAElementRecord[];
};

type CandidateChange = {
   candidate: AnvendelseCandidate;
   etagearealChange: number;
};

type OptimizerCandidate = AnvendelseCandidate & { effectPerEtageareal: number };

type AStarNode = {
   candidateIndex: number;
   changes: CandidateChange[];
   remainingValue: number;
   g: number;
   h: number;
   f: number;
   sequence: number;
};

type WarningLimit = {
   elementName: string;
   attributeId: string;
   message: string;
};

const PLA_OPTIMIZER_ZERO_TOLERANCE = 0.000001;
const PLA_OPTIMIZER_SEARCH_ITERATIONS = 24;
const PLA_OPTIMIZER_A_STAR_MAX_NODES = 500;
const PLA_OPTIMIZER_DEFAULT_YIELD_EVERY_NODES = 4;
const PLA_OPTIMIZER_DEFAULT_YIELD_EVERY_SEARCH_STEPS = 6;

function normalizeText(value: string | undefined): string {
   return (value || "").trim().toLowerCase();
}

function elementTypeMatches(project: PLAProject, element: PLAElementRecord, text: string) {
   const search = normalizeText(text);
   const elementType = project.elementTypes[element.elementType];
   return normalizeText(element.elementType).includes(search) || normalizeText(elementType?.name).includes(search);
}

function findAttributeId(project: PLAProject, element: PLAElementRecord, attributeName: string): string | undefined {
   const search = normalizeText(attributeName);
   const definitions = project.elementTypes[element.elementType]?.attributes || {};
   return Object.entries(definitions).find(([attributeId, definition]) => normalizeText(attributeId) === search || normalizeText(definition.name) === search)?.[0];
}

function findTargetAttributeId(project: PLAProject, element: PLAElementRecord, targetAttributeId = PLA_OPTIMIZER_TARGET_ATTRIBUTE): string | undefined {
   const definitions = project.elementTypes[element.elementType]?.attributes || {};
   return Object.entries(definitions).find(([attributeId, definition]) => attributeId === targetAttributeId || definition.name === targetAttributeId)?.[0];
}

function getObjectiveValue(value: number, goal: PLAOptimizerGoal): number {
   return goal === "maximize" ? -value : value;
}

function getAttributeValue(project: PLAProject, element: PLAElementRecord, elements: PLAElementRecord[], relations: PLARelationRecord[], attributeId: string): any {
   const definition = project.elementTypes[element.elementType]?.attributes?.[attributeId];
   if (definition?.type === "expression") return resolvePLACalculatedAttributes(element, project, elements, relations, { includeErrors: true })[attributeId];
   const value = element.attributes[attributeId];
   return value?.mode === "constant" ? value.value : value?.expression;
}

function setNumberAttribute(elements: PLAElementRecord[], elementId: string, attributeId: string, value: number): PLAElementRecord[] {
   return elements.map((element) => element.id !== elementId ? element : {
      ...element,
      attributes: {
         ...element.attributes,
         [attributeId]: { mode: "constant", valueType: "number", value }
      }
   });
}

function applyCandidateChanges(elements: PLAElementRecord[], changes: CandidateChange[]): PLAElementRecord[] {
   if (!changes.length) return elements;
   const valuesByElementId = new Map(changes.map((change) => [change.candidate.element.id, { attributeId: change.candidate.etagearealAttributeId, value: change.candidate.currentEtageareal + change.etagearealChange }]));
   return elements.map((element) => {
      const change = valuesByElementId.get(element.id);
      if (!change) return element;
      return {
         ...element,
         attributes: {
            ...element.attributes,
            [change.attributeId]: { mode: "constant", valueType: "number", value: change.value }
         }
      };
   });
}

function getOutgoingRelations(relations: PLARelationRecord[]): Record<string, PLARelationRecord[]> {
   return relations.reduce<Record<string, PLARelationRecord[]>>((acc, relation) => {
      acc[relation.fromId] = [...(acc[relation.fromId] || []), relation];
      return acc;
   }, {});
}

function collectAnvendelseCandidates(project: PLAProject, topElement: PLAElementRecord, elements: PLAElementRecord[], elementsById: Map<string, PLAElementRecord>, outgoingRelations: Record<string, PLARelationRecord[]>, relations: PLARelationRecord[], changeElementTypeName = "Anvendelse", changeAttributeName = "Etageareal"): AnvendelseCandidate[] {
   const candidates: AnvendelseCandidate[] = [];
   const visited = new Set<string>();

   const visit = (element: PLAElementRecord, containingByggefelt: PLAElementRecord | undefined, path: PLAElementRecord[]) => {
      if (visited.has(element.id)) return;
      visited.add(element.id);
      const nextByggefelt = elementTypeMatches(project, element, "Byggefelt") ? element : containingByggefelt;
      const nextPath = [...path, element];

      if (elementTypeMatches(project, element, changeElementTypeName)) {
         const etagearealAttributeId = findAttributeId(project, element, changeAttributeName);
         if (etagearealAttributeId) {
            const value = getAttributeValue(project, element, elements, relations, etagearealAttributeId);
            const currentEtageareal = typeof value === "number" ? value : Number(value);
            if (Number.isFinite(currentEtageareal)) candidates.push({ element, etagearealAttributeId, currentEtageareal, containingByggefelt: nextByggefelt, path: nextPath });
         }
      }

      (outgoingRelations[element.id] || []).forEach((relation) => {
         const child = elementsById.get(relation.toId);
         if (child) visit(child, nextByggefelt, nextPath);
      });
   };

   visit(topElement, undefined, []);
   return candidates;
}

function collectExpressionTrace(project: PLAProject, path: PLAElementRecord[], topAttributeId: string): string[] {
   const trace: string[] = [];
   path.forEach((element, index) => {
      const definitions = project.elementTypes[element.elementType]?.attributes || {};
      Object.entries(definitions).forEach(([attributeId, definition]) => {
         if (definition.type !== "expression") return;
         if (index === 0 && attributeId !== topAttributeId && definition.name !== PLA_OPTIMIZER_TARGET_ATTRIBUTE) return;
         trace.push(`${element.name}: ${definition.name || attributeId} = ${definition.expression || ""}`);
      });
   });
   return trace;
}

function getOptimizerWarningElementTypeIds(project: PLAProject): Set<string> {
   return new Set(Object.entries(project.elementTypes || {})
      .filter(([, elementType]) => Object.values(elementType.attributes || {}).some((attribute) => attribute.type === "expression" || Object.values(attribute.alerts || {}).some((alert) => !alert.optimizerMayOverride)))
      .map(([elementTypeId]) => elementTypeId));
}

function getWarningSignatures(project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[], warningElementTypeIds: Set<string>): Set<string> {
   return elements.reduce<Set<string>>((signatures, element) => {
      if (!warningElementTypeIds.has(element.elementType)) return signatures;
      getPLAAlertDetails(element, project, elements, relations).forEach((alert) => {
         if (alert.optimizerMayOverride) return;
         signatures.add(`${element.id}:${alert.attributeId}:${alert.message}`);
      });
      return signatures;
   }, new Set());
}

function getNewWarnings(project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[], baselineWarnings: Set<string>, warningElementTypeIds: Set<string>, elementsToCheck = elements): WarningLimit[] {
   return elementsToCheck.reduce<WarningLimit[]>((warnings, element) => {
      if (!warningElementTypeIds.has(element.elementType)) return warnings;
      getPLAAlertDetails(element, project, elements, relations).forEach((alert) => {
         if (alert.optimizerMayOverride) return;
         const signature = `${element.id}:${alert.attributeId}:${alert.message}`;
         if (!baselineWarnings.has(signature)) warnings.push({ elementName: element.name, attributeId: alert.attributeId, message: alert.message });
      });
      return warnings;
   }, []);
}

function addWarningLimits(stats: Map<string, PLAOptimizerWarningLimitStat>, warnings: WarningLimit[]) {
   warnings.forEach((warning) => {
      const id = `${warning.attributeId}:${warning.message}`;
      const existing = stats.get(id) || { id, message: warning.message, count: 0, examples: [] };
      existing.count += 1;
      const example = `${warning.elementName}: ${warning.attributeId}`;
      if (!existing.examples.includes(example) && existing.examples.length < 3) existing.examples.push(example);
      stats.set(id, existing);
   });
}

function getAffectedElements(elements: PLAElementRecord[], changes: CandidateChange[]): PLAElementRecord[] {
   if (!changes.length) return elements;
   const affectedIds = new Set<string>();
   changes.forEach((change) => change.candidate.path.forEach((element) => affectedIds.add(element.id)));
   return elements.filter((element) => affectedIds.has(element.id));
}

function getWarningFreeChange(project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[], baselineWarnings: Set<string>, warningElementTypeIds: Set<string>, warningLimitStats: Map<string, PLAOptimizerWarningLimitStat>, existingChanges: CandidateChange[], candidate: AnvendelseCandidate, desiredEtagearealChange: number): number {
   if (desiredEtagearealChange <= PLA_OPTIMIZER_ZERO_TOLERANCE) return desiredEtagearealChange;

   const fullChanges = [...existingChanges, { candidate, etagearealChange: desiredEtagearealChange }];
   const fullElements = applyCandidateChanges(elements, fullChanges);
   const fullWarnings = getNewWarnings(project, fullElements, relations, baselineWarnings, warningElementTypeIds, getAffectedElements(fullElements, fullChanges));
   if (!fullWarnings.length) return desiredEtagearealChange;
   addWarningLimits(warningLimitStats, fullWarnings);

   let low = 0;
   let high = desiredEtagearealChange;
   for (let index = 0; index < PLA_OPTIMIZER_SEARCH_ITERATIONS; index++) {
      const middle = (low + high) / 2;
      const middleChanges = [...existingChanges, { candidate, etagearealChange: middle }];
      const middleElements = applyCandidateChanges(elements, middleChanges);
      const warnings = getNewWarnings(project, middleElements, relations, baselineWarnings, warningElementTypeIds, getAffectedElements(middleElements, middleChanges));
      if (warnings.length) high = middle;
      else low = middle;
   }
   return low;
}

async function getWarningFreeChangeAsync(project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[], baselineWarnings: Set<string>, warningElementTypeIds: Set<string>, warningLimitStats: Map<string, PLAOptimizerWarningLimitStat>, existingChanges: CandidateChange[], candidate: AnvendelseCandidate, desiredEtagearealChange: number): Promise<number> {
   if (desiredEtagearealChange <= PLA_OPTIMIZER_ZERO_TOLERANCE) return desiredEtagearealChange;

   const fullChanges = [...existingChanges, { candidate, etagearealChange: desiredEtagearealChange }];
   const fullElements = applyCandidateChanges(elements, fullChanges);
   const fullWarnings = getNewWarnings(project, fullElements, relations, baselineWarnings, warningElementTypeIds, getAffectedElements(fullElements, fullChanges));
   if (!fullWarnings.length) return desiredEtagearealChange;
   addWarningLimits(warningLimitStats, fullWarnings);

   const yieldEverySearchSteps = PLA_OPTIMIZER_DEFAULT_YIELD_EVERY_SEARCH_STEPS;
   let low = 0;
   let high = desiredEtagearealChange;
   for (let index = 0; index < PLA_OPTIMIZER_SEARCH_ITERATIONS; index++) {
      if (index > 0 && index % yieldEverySearchSteps === 0) await waitForBrowser();
      const middle = (low + high) / 2;
      const middleChanges = [...existingChanges, { candidate, etagearealChange: middle }];
      const middleElements = applyCandidateChanges(elements, middleChanges);
      const warnings = getNewWarnings(project, middleElements, relations, baselineWarnings, warningElementTypeIds, getAffectedElements(middleElements, middleChanges));
      if (warnings.length) high = middle;
      else low = middle;
   }
   return low;
}

function createChange(candidate: AnvendelseCandidate, etagearealChange: number): PLAOptimizerChange {
   return {
      id: `${candidate.element.id}:${candidate.etagearealAttributeId}`,
      anvendelseElementId: candidate.element.id,
      anvendelseName: candidate.element.name,
      etagearealAttributeId: candidate.etagearealAttributeId,
      byggefeltElementId: candidate.containingByggefelt?.id,
      byggefeltName: candidate.containingByggefelt?.name,
      currentEtageareal: candidate.currentEtageareal,
      proposedEtageareal: candidate.currentEtageareal + etagearealChange,
      etagearealChange
   };
}

function getProposalChangeKey(proposal: PLAOptimizerProposal): string {
   return proposal.changes.map((change) => `${change.anvendelseElementId}:${change.etagearealChange.toFixed(6)}`).sort().join("|");
}

function createValidatedProposal(project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[], baselineWarnings: Set<string>, warningElementTypeIds: Set<string>, warningLimitStats: Map<string, PLAOptimizerWarningLimitStat>, topElement: PLAElementRecord, targetAttributeId: string, currentValue: number, currentObjectiveValue: number, goal: PLAOptimizerGoal, changes: CandidateChange[], proposalLabel: string, allowPartial = false): PLAOptimizerProposal | undefined {
   const changedElements = applyCandidateChanges(elements, changes);
   const newWarnings = getNewWarnings(project, changedElements, relations, baselineWarnings, warningElementTypeIds, getAffectedElements(changedElements, changes));
   if (newWarnings.length) {
      addWarningLimits(warningLimitStats, newWarnings);
      return undefined;
   }

   const nextTopValue = getAttributeValue(project, topElement, changedElements, relations, targetAttributeId);
   const nextNumericTopValue = typeof nextTopValue === "number" ? nextTopValue : Number(nextTopValue);
   if (!Number.isFinite(nextNumericTopValue)) return undefined;
   const nextObjectiveValue = getObjectiveValue(nextNumericTopValue, goal);
   const partial = Math.abs(nextObjectiveValue) > PLA_OPTIMIZER_ZERO_TOLERANCE;
   if (partial && (!allowPartial || Math.abs(nextObjectiveValue) >= Math.abs(currentObjectiveValue) - PLA_OPTIMIZER_ZERO_TOLERANCE)) return undefined;

   const optimizerChanges = changes.map((change) => createChange(change.candidate, change.etagearealChange));
   const expressionTrace = Array.from(new Set(changes.flatMap((change) => collectExpressionTrace(project, change.candidate.path, targetAttributeId))));
   const totalChange = optimizerChanges.reduce((sum, change) => sum + change.etagearealChange, 0);
   const changeDetails = Localization.getText("Change total Etageareal by {value}").replace("{value}", formatPLANumber(totalChange));

   return {
      id: `${topElement.id}:${targetAttributeId}:${proposalLabel}:${optimizerChanges.map((change) => change.id).join("|")}`,
      topElementId: topElement.id,
      topElementName: topElement.name,
      currentValue,
      remainingValue: nextNumericTopValue,
      partial,
      actionText: Localization.getText(proposalLabel),
      details: partial ? `${changeDetails}. ${Localization.getText("Remaining surplus after partial optimization is {value}").replace("{value}", formatPLANumber(nextObjectiveValue))}` : changeDetails,
      changes: optimizerChanges,
      expressionTrace
   };
}

function createAStarNode(candidateIndex: number, changes: CandidateChange[], remainingValue: number, sequence: number): AStarNode {
   const g = changes.length * PLA_OPTIMIZER_ZERO_TOLERANCE;
   const h = Math.abs(remainingValue);
   return { candidateIndex, changes, remainingValue, g, h, f: g + h, sequence };
}

function popBestAStarNode(open: AStarNode[]): AStarNode | undefined {
   if (!open.length) return undefined;
   let bestIndex = 0;
   for (let index = 1; index < open.length; index++) {
      const candidate = open[index];
      const best = open[bestIndex];
      if (candidate.f < best.f || (candidate.f === best.f && (candidate.h < best.h || (candidate.h === best.h && candidate.sequence < best.sequence)))) bestIndex = index;
   }
   const [node] = open.splice(bestIndex, 1);
   return node;
}

function getRemainingValue(project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[], topElement: PLAElementRecord, targetAttributeId: string, changes: CandidateChange[]): number | undefined {
   const changedElements = applyCandidateChanges(elements, changes);
   const nextValue = getAttributeValue(project, topElement, changedElements, relations, targetAttributeId);
   const nextNumericValue = typeof nextValue === "number" ? nextValue : Number(nextValue);
   return Number.isFinite(nextNumericValue) ? nextNumericValue : undefined;
}

function createGreedyProposal(project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[], baselineWarnings: Set<string>, warningElementTypeIds: Set<string>, warningLimitStats: Map<string, PLAOptimizerWarningLimitStat>, topElement: PLAElementRecord, targetAttributeId: string, currentValue: number, currentObjectiveValue: number, goal: PLAOptimizerGoal, candidates: OptimizerCandidate[]): PLAOptimizerProposal | undefined {
   let remainingObjectiveValue = currentObjectiveValue;
   const changes: CandidateChange[] = [];

   candidates.forEach((candidate) => {
      if (Math.abs(remainingObjectiveValue) <= PLA_OPTIMIZER_ZERO_TOLERANCE) return;
      const desiredEtagearealChange = -remainingObjectiveValue / candidate.effectPerEtageareal;
      if (!Number.isFinite(desiredEtagearealChange) || desiredEtagearealChange <= PLA_OPTIMIZER_ZERO_TOLERANCE) return;

      const etagearealChange = getWarningFreeChange(project, elements, relations, baselineWarnings, warningElementTypeIds, warningLimitStats, changes, candidate, desiredEtagearealChange);
      if (etagearealChange <= PLA_OPTIMIZER_ZERO_TOLERANCE) return;

      changes.push({ candidate, etagearealChange });
      const nextValue = getRemainingValue(project, elements, relations, topElement, targetAttributeId, changes);
      if (nextValue !== undefined) remainingObjectiveValue = getObjectiveValue(nextValue, goal);
   });

   if (!changes.length) return undefined;
   return createValidatedProposal(project, elements, relations, baselineWarnings, warningElementTypeIds, warningLimitStats, topElement, targetAttributeId, currentValue, currentObjectiveValue, goal, changes, Math.abs(remainingObjectiveValue) > PLA_OPTIMIZER_ZERO_TOLERANCE ? "Partial optimizer proposal" : "Change several Anvendelse Etageareal values", true);
}

function createAStarProposal(project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[], baselineWarnings: Set<string>, warningElementTypeIds: Set<string>, warningLimitStats: Map<string, PLAOptimizerWarningLimitStat>, topElement: PLAElementRecord, targetAttributeId: string, currentValue: number, currentObjectiveValue: number, goal: PLAOptimizerGoal, candidates: OptimizerCandidate[]): PLAOptimizerProposal | undefined {
   const open: AStarNode[] = [createAStarNode(0, [], currentObjectiveValue, 0)];
   let nextSequence = 1;
   let visitedNodes = 0;
   let bestPartial: AStarNode | undefined;

   while (open.length && visitedNodes < PLA_OPTIMIZER_A_STAR_MAX_NODES) {
      const node = popBestAStarNode(open);
      if (!node) break;
      visitedNodes += 1;

      if (node.changes.length && (!bestPartial || node.h < bestPartial.h)) bestPartial = node;
      if (node.changes.length && node.h <= PLA_OPTIMIZER_ZERO_TOLERANCE) {
         return createValidatedProposal(project, elements, relations, baselineWarnings, warningElementTypeIds, warningLimitStats, topElement, targetAttributeId, currentValue, currentObjectiveValue, goal, node.changes, "Change several Anvendelse Etageareal values", true);
      }
      if (node.candidateIndex >= candidates.length) continue;

      const candidate = candidates[node.candidateIndex];
      open.push(createAStarNode(node.candidateIndex + 1, node.changes, node.remainingValue, nextSequence++));

      const desiredEtagearealChange = -node.remainingValue / candidate.effectPerEtageareal;
      if (!Number.isFinite(desiredEtagearealChange) || desiredEtagearealChange <= PLA_OPTIMIZER_ZERO_TOLERANCE) continue;

      const etagearealChange = getWarningFreeChange(project, elements, relations, baselineWarnings, warningElementTypeIds, warningLimitStats, node.changes, candidate, desiredEtagearealChange);
      if (etagearealChange <= PLA_OPTIMIZER_ZERO_TOLERANCE) continue;

      const nextChanges = [...node.changes, { candidate, etagearealChange }];
      const nextRemainingValue = getRemainingValue(project, elements, relations, topElement, targetAttributeId, nextChanges);
      if (nextRemainingValue === undefined) continue;
      open.push(createAStarNode(node.candidateIndex + 1, nextChanges, getObjectiveValue(nextRemainingValue, goal), nextSequence++));
   }

   if (!bestPartial) return undefined;
   return createValidatedProposal(project, elements, relations, baselineWarnings, warningElementTypeIds, warningLimitStats, topElement, targetAttributeId, currentValue, currentObjectiveValue, goal, bestPartial.changes, "Partial optimizer proposal", true);
}

function waitForBrowser() {
   return new Promise<void>((resolve) => setTimeout(resolve, 0));
}

async function createAStarProposalAsync(project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[], baselineWarnings: Set<string>, warningElementTypeIds: Set<string>, warningLimitStats: Map<string, PLAOptimizerWarningLimitStat>, topElement: PLAElementRecord, targetAttributeId: string, currentValue: number, currentObjectiveValue: number, goal: PLAOptimizerGoal, candidates: OptimizerCandidate[], progressBase: number, progressSpan: number, options: PLAOptimizationOptions): Promise<PLAOptimizerProposal | undefined> {
   const open: AStarNode[] = [createAStarNode(0, [], currentObjectiveValue, 0)];
   const yieldEveryNodes = options.yieldEveryNodes || PLA_OPTIMIZER_DEFAULT_YIELD_EVERY_NODES;
   let nextSequence = 1;
   let visitedNodes = 0;
   let bestPartial: AStarNode | undefined;

   while (open.length && visitedNodes < PLA_OPTIMIZER_A_STAR_MAX_NODES) {
      const node = popBestAStarNode(open);
      if (!node) break;
      visitedNodes += 1;

      if (visitedNodes % yieldEveryNodes === 0) {
         const current = progressBase + (Math.min(visitedNodes, PLA_OPTIMIZER_A_STAR_MAX_NODES) / PLA_OPTIMIZER_A_STAR_MAX_NODES) * progressSpan;
         options.onProgress?.({ current, total: 100, percent: current, message: Localization.getText("Evaluating optimizer proposals") });
         await waitForBrowser();
      }

      if (node.changes.length && (!bestPartial || node.h < bestPartial.h)) bestPartial = node;
      if (node.changes.length && node.h <= PLA_OPTIMIZER_ZERO_TOLERANCE) {
         return createValidatedProposal(project, elements, relations, baselineWarnings, warningElementTypeIds, warningLimitStats, topElement, targetAttributeId, currentValue, currentObjectiveValue, goal, node.changes, "Change several Anvendelse Etageareal values", true);
      }
      if (node.candidateIndex >= candidates.length) continue;

      const candidate = candidates[node.candidateIndex];
      open.push(createAStarNode(node.candidateIndex + 1, node.changes, node.remainingValue, nextSequence++));

      const desiredEtagearealChange = -node.remainingValue / candidate.effectPerEtageareal;
      if (!Number.isFinite(desiredEtagearealChange) || desiredEtagearealChange <= PLA_OPTIMIZER_ZERO_TOLERANCE) continue;

      const etagearealChange = await getWarningFreeChangeAsync(project, elements, relations, baselineWarnings, warningElementTypeIds, warningLimitStats, node.changes, candidate, desiredEtagearealChange);
      if (etagearealChange <= PLA_OPTIMIZER_ZERO_TOLERANCE) continue;

      const nextChanges = [...node.changes, { candidate, etagearealChange }];
      const nextRemainingValue = getRemainingValue(project, elements, relations, topElement, targetAttributeId, nextChanges);
      if (nextRemainingValue === undefined) continue;
      open.push(createAStarNode(node.candidateIndex + 1, nextChanges, getObjectiveValue(nextRemainingValue, goal), nextSequence++));
   }

   if (!bestPartial) return undefined;
   return createValidatedProposal(project, elements, relations, baselineWarnings, warningElementTypeIds, warningLimitStats, topElement, targetAttributeId, currentValue, currentObjectiveValue, goal, bestPartial.changes, "Partial optimizer proposal", true);
}

function collectOptimizerCandidates(project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[], elementsById: Map<string, PLAElementRecord>, outgoingRelations: Record<string, PLARelationRecord[]>, topElement: PLAElementRecord, targetAttributeId: string, numericValue: number, goal: PLAOptimizerGoal, changeElementTypeName: string, changeAttributeName: string): OptimizerCandidate[] {
   return collectAnvendelseCandidates(project, topElement, elements, elementsById, outgoingRelations, relations, changeElementTypeName, changeAttributeName).map((candidate) => {
      const testElements = setNumberAttribute(elements, candidate.element.id, candidate.etagearealAttributeId, candidate.currentEtageareal + 1);
      const testValue = getAttributeValue(project, topElement, testElements, relations, targetAttributeId);
      const testNumericValue = typeof testValue === "number" ? testValue : Number(testValue);
      if (!Number.isFinite(testNumericValue)) return undefined;
      const effectPerEtageareal = getObjectiveValue(testNumericValue, goal) - getObjectiveValue(numericValue, goal);
      if (!Number.isFinite(effectPerEtageareal) || effectPerEtageareal === 0) return undefined;
      return { ...candidate, effectPerEtageareal };
   }).filter((candidate): candidate is OptimizerCandidate => Boolean(candidate));
}

export function createPLAOptimizationResult(project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[]): PLAOptimizationResult {
   const parameters: Required<PLAOptimizerParameters> = { targetAttributeId: PLA_OPTIMIZER_TARGET_ATTRIBUTE, goal: "minimize", algorithm: "astar", changeElementTypeName: "Anvendelse", changeAttributeName: "Etageareal" };
   const hierarchicalRelations = getPLAHierarchicalRelations(project, relations);
   const incomingIds = new Set(hierarchicalRelations.map((relation) => relation.toId));
   const topLevelElements = elements.filter((element) => !incomingIds.has(element.id));
   const elementsById = new Map(elements.map((element) => [element.id, element]));
   const outgoingRelations = getOutgoingRelations(hierarchicalRelations);
   const warningElementTypeIds = getOptimizerWarningElementTypeIds(project);
   const baselineWarnings = getWarningSignatures(project, elements, relations, warningElementTypeIds);
   const warningLimitStats = new Map<string, PLAOptimizerWarningLimitStat>();

   const proposals = topLevelElements.reduce<PLAOptimizerProposal[]>((proposals, element) => {
      const attributeId = findTargetAttributeId(project, element, parameters.targetAttributeId);
      if (!attributeId) return proposals;

      const value = getAttributeValue(project, element, elements, relations, attributeId);
      const numericValue = typeof value === "number" ? value : Number(value);
      if (!Number.isFinite(numericValue) || numericValue === 0) return proposals;
      const objectiveValue = getObjectiveValue(numericValue, parameters.goal);
      if (objectiveValue <= PLA_OPTIMIZER_ZERO_TOLERANCE) return proposals;

      const candidates = collectOptimizerCandidates(project, elements, relations, elementsById, outgoingRelations, element, attributeId, numericValue, parameters.goal, parameters.changeElementTypeName, parameters.changeAttributeName);

      candidates.forEach((candidate) => {
         const etagearealChange = -objectiveValue / candidate.effectPerEtageareal;
         if (!Number.isFinite(etagearealChange)) return;
         const proposal = createValidatedProposal(project, elements, relations, baselineWarnings, warningElementTypeIds, warningLimitStats, element, attributeId, numericValue, objectiveValue, parameters.goal, [{ candidate, etagearealChange }], "Change one Anvendelse Etageareal");
         if (proposal) proposals.push(proposal);
      });

      if (candidates.length > 1) {
         const contributionPerCandidate = -objectiveValue / candidates.length;
         const balancedChanges = candidates.map((candidate) => ({ candidate, etagearealChange: contributionPerCandidate / candidate.effectPerEtageareal }));
         const proposal = createValidatedProposal(project, elements, relations, baselineWarnings, warningElementTypeIds, warningLimitStats, element, attributeId, numericValue, objectiveValue, parameters.goal, balancedChanges, "Change several Anvendelse Etageareal values");
         if (proposal) proposals.push(proposal);
      }

      const partialProposal = parameters.algorithm === "greedy"
         ? createGreedyProposal(project, elements, relations, baselineWarnings, warningElementTypeIds, warningLimitStats, element, attributeId, numericValue, objectiveValue, parameters.goal, candidates)
         : createAStarProposal(project, elements, relations, baselineWarnings, warningElementTypeIds, warningLimitStats, element, attributeId, numericValue, objectiveValue, parameters.goal, candidates);
      if (partialProposal && !proposals.some((proposal) => getProposalChangeKey(proposal) === getProposalChangeKey(partialProposal))) proposals.push(partialProposal);

      return proposals;
   }, []);

   return {
      proposals,
      warningLimitStats: Array.from(warningLimitStats.values()).sort((a, b) => b.count - a.count || a.message.localeCompare(b.message)),
      parameters
   };
}

export async function createPLAOptimizationResultAsync(project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[], options: PLAOptimizationOptions = {}): Promise<PLAOptimizationResult> {
   const parameters: Required<PLAOptimizerParameters> = { targetAttributeId: options.targetAttributeId || PLA_OPTIMIZER_TARGET_ATTRIBUTE, goal: options.goal || "minimize", algorithm: options.algorithm || "astar", changeElementTypeName: options.changeElementTypeName || "Anvendelse", changeAttributeName: options.changeAttributeName || "Etageareal" };
   const hierarchicalRelations = getPLAHierarchicalRelations(project, relations);
   const incomingIds = new Set(hierarchicalRelations.map((relation) => relation.toId));
   const topLevelElements = elements.filter((element) => !incomingIds.has(element.id));
   const elementsById = new Map(elements.map((element) => [element.id, element]));
   const outgoingRelations = getOutgoingRelations(hierarchicalRelations);
   const warningElementTypeIds = getOptimizerWarningElementTypeIds(project);
   const baselineWarnings = getWarningSignatures(project, elements, relations, warningElementTypeIds);
   const warningLimitStats = new Map<string, PLAOptimizerWarningLimitStat>();
   const proposals: PLAOptimizerProposal[] = [];
   const total = Math.max(topLevelElements.length, 1);

   options.onProgress?.({ current: 0, total: 100, percent: 0, message: Localization.getText("Preparing optimizer") });
   await waitForBrowser();

   for (let index = 0; index < topLevelElements.length; index++) {
      const element = topLevelElements[index];
      const progressBase = (index / total) * 100;
      const progressSpan = 100 / total;
      options.onProgress?.({ current: progressBase, total: 100, percent: progressBase, message: Localization.getText("Evaluating optimizer proposals") });
      await waitForBrowser();

      const attributeId = findTargetAttributeId(project, element, parameters.targetAttributeId);
      if (!attributeId) continue;

      const value = getAttributeValue(project, element, elements, relations, attributeId);
      const numericValue = typeof value === "number" ? value : Number(value);
      if (!Number.isFinite(numericValue) || numericValue === 0) continue;
      const objectiveValue = getObjectiveValue(numericValue, parameters.goal);
      if (objectiveValue <= PLA_OPTIMIZER_ZERO_TOLERANCE) continue;

      const candidates = collectOptimizerCandidates(project, elements, relations, elementsById, outgoingRelations, element, attributeId, numericValue, parameters.goal, parameters.changeElementTypeName, parameters.changeAttributeName);

      candidates.forEach((candidate) => {
         const etagearealChange = -objectiveValue / candidate.effectPerEtageareal;
         if (!Number.isFinite(etagearealChange)) return;
         const proposal = createValidatedProposal(project, elements, relations, baselineWarnings, warningElementTypeIds, warningLimitStats, element, attributeId, numericValue, objectiveValue, parameters.goal, [{ candidate, etagearealChange }], "Change one Anvendelse Etageareal");
         if (proposal) proposals.push(proposal);
      });

      if (candidates.length > 1) {
         const contributionPerCandidate = -objectiveValue / candidates.length;
         const balancedChanges = candidates.map((candidate) => ({ candidate, etagearealChange: contributionPerCandidate / candidate.effectPerEtageareal }));
         const proposal = createValidatedProposal(project, elements, relations, baselineWarnings, warningElementTypeIds, warningLimitStats, element, attributeId, numericValue, objectiveValue, parameters.goal, balancedChanges, "Change several Anvendelse Etageareal values");
         if (proposal) proposals.push(proposal);
      }

      const partialProposal = parameters.algorithm === "greedy"
         ? createGreedyProposal(project, elements, relations, baselineWarnings, warningElementTypeIds, warningLimitStats, element, attributeId, numericValue, objectiveValue, parameters.goal, candidates)
         : await createAStarProposalAsync(project, elements, relations, baselineWarnings, warningElementTypeIds, warningLimitStats, element, attributeId, numericValue, objectiveValue, parameters.goal, candidates, progressBase, progressSpan, options);
      if (partialProposal && !proposals.some((proposal) => getProposalChangeKey(proposal) === getProposalChangeKey(partialProposal))) proposals.push(partialProposal);
   }

   options.onProgress?.({ current: 100, total: 100, percent: 100, message: Localization.getText("Optimizer running") });
   return {
      proposals,
      warningLimitStats: Array.from(warningLimitStats.values()).sort((a, b) => b.count - a.count || a.message.localeCompare(b.message)),
      parameters
   };
}

export function createPLAOptimizationProposals(project: PLAProject, elements: PLAElementRecord[], relations: PLARelationRecord[]): PLAOptimizerProposal[] {
   return createPLAOptimizationResult(project, elements, relations).proposals;
}